import { deleteRequest, getRequest, patchRequest, postRequest, putRequest } from '../request/request.service';
import {
  AgentDocument,
  AgentParams,
  AgentResponse,
  AgentsResponse,
  AgentUsage,
  CreateAgentPayload,
  UpdateAgentPayload,
  TestCallPayload,
  AddKbTextPayload,
  AddKbUrlPayload,
  AddKbFilePayload,
  PrefillResult,
  TransferableDocumentsResponse,
  TransferKbPayload,
  TransferKbResponse,
  UpdateCallHandlerPayload,
  CallHandlerResponse,
  UpdateCommandCenterHandoffPayload,
  CommandCenterHandoffResponse,
  LanguageVoicesResponse,
  UpdateLanguageVoicesPayload,
  VoiceHealthResponse,
  TransferAnnouncementsResponse,
  FillerClipsResponse,
  AudioClipTextsResponse,
  AudioClipType,
} from './agents.service.types';

const AGENTS_URL = 'conversational_agents';

export function getAgents(params: AgentParams) {
  return getRequest<AgentsResponse>(`/${AGENTS_URL}`, { params });
}

export function getAgent(id: string, params: AgentParams) {
  return getRequest<AgentResponse>(`/${AGENTS_URL}/${id}`, { params });
}

export function createAgent(data: CreateAgentPayload) {
  return postRequest<AgentResponse>(`/${AGENTS_URL}`, { data });
}

export function updateAgent(id: string, params: AgentParams, data: UpdateAgentPayload) {
  return putRequest<AgentResponse>(`/${AGENTS_URL}/${id}`, { params, data });
}

export function deleteAgent(id: number, params: AgentParams) {
  return deleteRequest(`/${AGENTS_URL}/${id}`, { params });
}

export function publishAgent(id: string, params: AgentParams) {
  return postRequest<AgentResponse>(`/${AGENTS_URL}/${id}/publish`, { params });
}

export function syncAgent(id: string, params: AgentParams) {
  return postRequest<AgentResponse>(`/${AGENTS_URL}/${id}/sync`, { params });
}

export function unpublishAgent(id: string, params: AgentParams) {
  return postRequest<AgentResponse>(`/${AGENTS_URL}/${id}/unpublish`, { params });
}

export function getAgentUsage(id: string, params: AgentParams) {
  return getRequest<AgentUsage>(`/${AGENTS_URL}/${id}/usage`, { params });
}

export function testCall(id: string, data: TestCallPayload) {
  return postRequest(`/${AGENTS_URL}/${id}/test_call`, { data });
}

export function addKbText(id: string, data: AddKbTextPayload) {
  return postRequest(`/${AGENTS_URL}/${id}/kb/text`, { data });
}

export function addKbUrl(id: string, data: AddKbUrlPayload) {
  return postRequest(`/${AGENTS_URL}/${id}/kb/url`, { data });
}

export function addKbFile(id: string, data: AddKbFilePayload) {
  const formData = new FormData();
  formData.append('company_id', String(data.company_id));
  formData.append('file', data.file);
  if (data.name) formData.append('name', data.name);
  return postRequest(`/${AGENTS_URL}/${id}/kb/file`, {
    data: formData,
  });
}

export function removeKbDoc(id: string, docId: number, params: AgentParams) {
  return deleteRequest(`/${AGENTS_URL}/${id}/kb/${docId}`, { params });
}

export function checkKbStatus(id: string, docId: number, params: AgentParams) {
  return postRequest<{ document: AgentDocument }>(`/${AGENTS_URL}/${id}/kb/${docId}/check_status`, { params });
}

export function getPrefillInstructions(params: AgentParams) {
  return getRequest<PrefillResult>(`/${AGENTS_URL}/prefill`, { params });
}

export function getTransferableDocuments(id: string, params: AgentParams) {
  return getRequest<TransferableDocumentsResponse>(`/${AGENTS_URL}/${id}/transferable_documents`, { params });
}

export function transferKb(id: string, data: TransferKbPayload) {
  return postRequest<TransferKbResponse>(`/${AGENTS_URL}/${id}/transfer_kb`, { data });
}

export function updateCallHandler(data: UpdateCallHandlerPayload) {
  return patchRequest<CallHandlerResponse>(`/${AGENTS_URL}/update_call_handler`, { data });
}

export function updateCommandCenterHandoff(data: UpdateCommandCenterHandoffPayload) {
  return patchRequest<CommandCenterHandoffResponse>(
    `/${AGENTS_URL}/update_command_center_handoff`,
    { data },
  );
}

export function getLanguageVoices(params: AgentParams) {
  return getRequest<LanguageVoicesResponse>(`/${AGENTS_URL}/language_voices`, { params });
}

export function updateLanguageVoices(data: UpdateLanguageVoicesPayload) {
  return patchRequest<LanguageVoicesResponse>(`/${AGENTS_URL}/update_language_voices`, { data });
}

export function getTransferAnnouncements(id: string, params: AgentParams) {
  return getRequest<TransferAnnouncementsResponse>(`/${AGENTS_URL}/${id}/transfer_announcements`, { params });
}

export function generateTransferAnnouncements(id: string, params: AgentParams & { force?: boolean }) {
  return postRequest(`/${AGENTS_URL}/${id}/transfer_announcements/generate`, { data: params });
}

export function getFillerClips(id: string, params: AgentParams) {
  return getRequest<FillerClipsResponse>(`/${AGENTS_URL}/${id}/filler_clips`, { params });
}

export function getVoiceHealth(id: string, params: AgentParams) {
  return getRequest<VoiceHealthResponse>(`/${AGENTS_URL}/${id}/voice_health`, { params });
}

export function getAudioClipTexts(id: string, params: AgentParams) {
  return getRequest<AudioClipTextsResponse>(`/${AGENTS_URL}/${id}/audio_clip_texts`, { params });
}

export function updateAudioClipText(
  id: string,
  params: AgentParams & { clip_type: AudioClipType; text: string }
) {
  return patchRequest(`/${AGENTS_URL}/${id}/audio_clip_texts`, { data: params });
}

export function destroyAudioClipText(
  id: string,
  params: AgentParams & { clip_type: AudioClipType }
) {
  return deleteRequest(`/${AGENTS_URL}/${id}/audio_clip_texts`, { params });
}

// Fetch the µ-law-as-WAV preview blob for a single filler clip. Returned
// blob is converted into a blob URL on the dashboard side and fed to
// <audio>. Auth is handled by axios — direct browser fetch wouldn't carry
// the access token.
export function getFillerClipPreviewBlob(
  id: string,
  params: AgentParams & { voice_id: string; lang_code: string }
) {
  return getRequest<Blob>(`/${AGENTS_URL}/${id}/filler_clip_preview`, {
    params,
    responseType: 'blob',
  });
}
